import React, {useContext, useEffect, useState} from "react";
import {MapViewContext} from "../contexts/MapViewContext";
import "./SelectedFilters.css";
import {TagCountsGrouped} from "../models/TagCountsGrouped";

interface SelectedFiltersProps {
    categoryCounts: TagCountsGrouped;
}

const SelectedFilters = ({categoryCounts}: SelectedFiltersProps) => {
    const {
        selectedCategoryIds,
        setSelectedCategoryIds,
        selectedTagIds,
        setSelectedTagIds,
        searchQuery,
        setSearchQuery,
    } = useContext(MapViewContext);

    const [selectedItems, setSelectedItems] = useState<
        { id: string; name: string; type: "category" | "tag"; color?: string }[]
    >([]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const catIds = params.get("categories_ids");
        const tagIds = params.get("tag_ids");
        const search = params.get("search");

        if (catIds) setSelectedCategoryIds(catIds.split(","));
        if (tagIds) setSelectedTagIds(tagIds.split(","));
        if (search) setSearchQuery(search);
    }, []);

    // Build visible selected items whenever context changes
    useEffect(() => {
        const selected: {
            id: string;
            name: string;
            type: "category" | "tag";
            color?: string;
        }[] = [];

        Object.entries(categoryCounts).forEach(([catId, cat]) => {
            if (selectedCategoryIds.includes(catId)) {
                selected.push({id: catId, name: cat.name, type: "category"});
            }
            Object.entries(cat.tags).forEach(([tagId, tag]) => {
                if (selectedTagIds.includes(tagId)) {
                    selected.push({
                        id: tagId,
                        name: tag.name,
                        type: "tag",
                        color: tag.color,
                    });
                }
            });
        });

        setSelectedItems(selected);
    }, [selectedCategoryIds, selectedTagIds, categoryCounts]);

    const removeFilter = (id: string, type: "category" | "tag") => {
        const url = new URL(window.location.href);

        if (type === "category") {
            const newSelected = selectedCategoryIds.filter((c) => c !== id);
            if (newSelected.length > 0)
                url.searchParams.set("categories_ids", newSelected.join(","));
            else url.searchParams.delete("categories_ids");

            window.history.replaceState(null, "", url.toString());
            setSelectedCategoryIds(newSelected);
        }

        if (type === "tag") {
            const newSelected = selectedTagIds.filter((t) => t !== id);
            if (newSelected.length > 0)
                url.searchParams.set("tag_ids", newSelected.join(","));
            else url.searchParams.delete("tag_ids");

            window.history.replaceState(null, "", url.toString());
            setSelectedTagIds(newSelected);
        }
    };

    const clearSearch = () => {
        setSearchQuery("");
        const url = new URL(window.location.href);
        url.searchParams.delete("search");
        window.history.replaceState(null, "", url.toString());
    };

    const clearAll = () => {
        setSelectedCategoryIds([]);
        setSelectedTagIds([]);
        setSearchQuery("");

        const url = new URL(window.location.href);
        url.searchParams.delete("categories_ids");
        url.searchParams.delete("tag_ids");
        url.searchParams.delete("search");
        window.history.replaceState(null, "", url.toString());
    };

    if (
        selectedItems.length === 0
        && searchQuery !== undefined
        && searchQuery !== null
        && searchQuery.trim() === "") {
        return null;
    }

    return (
        <div className="selected-filters">
            <div className="filters-list">
                {searchQuery && searchQuery.trim() !== "" && (
                    <div className="filter-chip search-chip">
                        <span className="chip-label">
                            <strong>Search:</strong> {searchQuery}
                        </span>
                        <button className="chip-remove" onClick={clearSearch}>
                            ✕
                        </button>
                    </div>
                )}
                {selectedItems.map((item) => (
                    <div key={item.id} className="filter-chip">
                        {item.type === "tag" && (
                            <span
                                className="chip-color"
                                style={{backgroundColor: item.color}}
                            />
                        )}
                        <span className="chip-label">{item.name}</span>
                        <button
                            className="chip-remove"
                            onClick={() => removeFilter(item.id, item.type)}
                        >
                            ✕
                        </button>
                    </div>
                ))}
            </div>
            <button className="clear-all" onClick={clearAll}>
                Clear all
            </button>
        </div>
    );
};

export default SelectedFilters;
